<template>
  <p-child namespace="file-handling" class="p-form-element">
    <p-form-element
      class="p-file-drop"
      show-messages
      :errors="innerErrors"
      :messages="innerMessages"
    >
      <div v-if="label" :id="'file-drop-label-' + _uid" class="text-field-label">
        {{ label }}
      </div>
      <div
        ref="pFileDrop"
        :aria-labelledby="'file-drop-label-' + _uid"
        :class="classList"
        @dragenter.prevent="onDragEnter($event)"
        @dragleave.prevent="onDragLeave($event)"
        @dragover.prevent="onDragOver($event)"
        @drop.prevent="onDragDrop($event)"
        @dragend.prevent="onDragEnd($event)"
        @dragstart.prevent="onDragStart($event)"
      >
        <template v-if="providedFiles.length === 0">
          <p-row justify-center>
            <p-icon :color="iconColor" :disabled="disabled" class="file-drop-icon" spacious>
              {{ acceptsImages ? 'add_a_photo' : 'note_add' }}
            </p-icon>
          </p-row>
          <p-row v-if="!$media.isMobile && !computedLoading" justify-center>
            <span class="caption-1">{{ $t('core.PFileDrop.dragAFileHere') }}</span>
          </p-row>
        </template>
        <p-row v-if="computedLoading" class="my-4" justify-center>
          <p-progress-linear class="ix-4" indeterminate></p-progress-linear>
        </p-row>
        <p-row v-else>
          <p-col v-for="(file, i) in providedFiles" :key="i" justify-center>
            <p-card class="file-drop-card">
              <p-img class="fill-height" contain :src="file">
                <template slot="error">
                  <p-row justify-center align-center fill-height>
                    <p-icon class="ma-4" spacious>
                      {{ file.type.includes('pdf') ? 'picture_as_pdf' : 'description' }}
                    </p-icon>
                  </p-row>
                </template>
              </p-img>
              <p-button class="file-drop-remove" dense @click="onRemoveClick(file)">
                <p-icon>close</p-icon>
              </p-button>
              <div class="file-drop-details">
                <p-row justify-center align-end no-gutters>
                  <p-col xs12>
                    <span :title="file.name" class="file-drop-filename">
                      {{ $format.ellipsis(file.name, 40) }}
                    </span>
                  </p-col>
                  <p-col xs12 justify-end>
                    <span class="file-drop-size">{{ $format.bytesPretty(file.size) }}</span>
                  </p-col>
                </p-row>
              </div>
            </p-card>
          </p-col>
        </p-row>
        <p-row v-if="!hasMaxFiles" justify-center @dragenter.prevent="onDragEnter($event)">
          <p-file-selection
            :accept="accept"
            :multiple="!single"
            :capture="capture"
            :disabled="!canAddFiles"
            @input="onFileSelected"
          >
            {{ $t('core.PFileDrop.selectFile') }}
          </p-file-selection>
        </p-row>
      </div>
    </p-form-element>
  </p-child>
</template>

<script lang="ts">
  import mixins from '../../mixins';
  import validatable from '../../mixins/validatable/validatable';

  export default mixins(validatable).extend({
    props: {
      value: { default: () => ([]), type: [Array, Object] },
      loading: { default: false, type: Boolean },
      label: { default: undefined, type: String },
      /**
       * In Pixels
       */
      // minResolution: { default: undefined, type: [Number, String] },
      /**
       * In Pixels
       */
      // maxResolution: { default: undefined, type: [Number, String] },
      /**
       * Max number of files allowed (inclusive)
       */
      max: { default: 20, type: [Number, String] },
      /**
       * Which fileType can be uploaded
       * @example ".png"
       */
      accept: { default: undefined, type: [Array, String] },
      /**
       * In Megabytes
       */
      maxFileSize: { default: 5, type: [Number, String] },
      /**
       * In Megabytes
       */
      minFileSize: { default: undefined, type: [Number, String] },
      disabled: { type: Boolean, default: false },
      readonly: { type: Boolean, default: false },
      single: { type: Boolean, default: false },
      capture: {
        type: String,
        default: 'environment',
        // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/capture
        validator: (value : string) => value === 'environment' || value === 'user',
      },
    },
    data: () => ({
      isInDragMode: false,
      innerLoading: false,
      responseError: undefined as any,
      providedFiles: [] as File[],
      docIdToFileMap: {} as Record<string, File>,
    }),
    computed: {
      computedLoading(): boolean {
        return this.loading || this.innerLoading;
      },
      iconColor() {
        if (this.isInDragMode && this.canAddFiles) {
          return 'interaction';
        }
        if (typeof this.innerErrors === 'string') {
          return 'error';
        }
        if (Array.isArray(this.innerErrors) && this.innerErrors.length > 0) {
          return 'error';
        }
        return undefined;
      },
      maxNumber() {
        if (!this.max) {
          return undefined;
        }
        return Number.parseInt(this.max.toString(), 10);
      },
      hasMaxFiles() {
        if (this.single && this.providedFiles.length > 0) {
          return true;
        }
        if (this.maxNumber && this.maxNumber <= this.providedFiles.length) {
          return true;
        }
        return false;
      },
      canAddFiles() {
        if (this.disabled || this.readonly || this.computedLoading) {
          return false;
        } if (this.hasMaxFiles) {
          return false;
        }
        return true;
      },
      acceptsImages() {
        if (!this.accept) {
          return false;
        }
        let acceptsImages = false;
        const accept = (Array.isArray(this.accept) ? this.accept.join('') : this.accept).toLowerCase();
        acceptsImages = acceptsImages || accept.includes('png');
        acceptsImages = acceptsImages || accept.includes('jpg');
        acceptsImages = acceptsImages || accept.includes('jpeg');
        acceptsImages = acceptsImages || accept.includes('tiff');
        acceptsImages = acceptsImages || accept.includes('webp');
        acceptsImages = acceptsImages || accept.includes('gif');
        acceptsImages = acceptsImages || accept.includes('svg');
        acceptsImages = acceptsImages || accept.includes('bmp');
        acceptsImages = acceptsImages || accept.includes('image');
        return acceptsImages;
      },
      classList(): {} {
        return {
          'p-file-drop-zone': true,
          disabled: this.disabled,
          'dragging-file': this.isInDragMode && this.canAddFiles,
          'max-capacity': !this.canAddFiles,
          'color-readonly': this.disabled,
        };
      },
    },
    watch: {
      value: {
        immediate: true,
        handler() {
          if (Array.isArray(this.value)) {
            this.providedFiles = this.value;
          } else if (this.value) {
            this.providedFiles = [this.value];
          }
        },
      },
    },
    methods: {
      onRemoveClick(file: File) {
        this.removeFile(file);
      },
      onFileSelected(file: File | File[]) {
        this.addMultipleFiles(file);
      },
      removeFile(file: File) {
        const countBefore = this.providedFiles.length;
        this.providedFiles = this.providedFiles.filter((f) => f !== file);
        if (countBefore !== this.providedFiles.length) {
          this.$emit('input', this.providedFiles);
          this.$emit('remove-file', file);
          Object.keys(this.docIdToFileMap).forEach((docId) => {
            if (this.docIdToFileMap[docId] === file) {
              delete this.docIdToFileMap[docId];
            }
          });
        }
      },
      addSingleFile(file: File, clearErrors = true) {
        if (!this.canAddFiles) {
          return;
        }
        if (clearErrors) {
          this.innerErrors = undefined;
        }
        const fileSizeInMB = (file?.size ?? 1) / 1000000;
        if (fileSizeInMB > Number.parseFloat(this.maxFileSize?.toString())) {
          this.innerErrors = this.$t('core.PFileDrop.fileTooLarge');
          return;
        }
        if (fileSizeInMB < Number.parseFloat(this.minFileSize?.toString())) {
          this.innerErrors = this.$t('core.PFileDrop.fileTooSmall');
          return;
        }
        this.providedFiles.push(file);
        this.$nextTick(() => {
          this.$emit('add-file', file);
        });
      },
      addMultipleFiles(files: File | File[]) {
        if (!this.canAddFiles) {
          return;
        }
        this.innerErrors = undefined;
        if (Array.isArray(files)) {
          files.forEach((file) => {
            this.addSingleFile(file, false);
          });
        } else {
          this.addSingleFile(files, false);
        }
        this.$emit('input', this.providedFiles);
      },
      onDragEnter(event: DragEvent) {
        if (event.dataTransfer) {
          if (this.canAddFiles) {
            event.dataTransfer.dropEffect = 'copy';
          } else {
            event.dataTransfer.dropEffect = 'none';
          }
          event.dataTransfer.effectAllowed = 'all';
          this.isInDragMode = true;
        }
      },
      onDragLeave(event: DragEvent) {
        let parent = event.relatedTarget! as HTMLElement | undefined | null;
        while (parent) {
          if (parent === this.$refs.pFileDrop) {
            return;
          }
          parent = (parent as HTMLElement).parentElement;
        }
        this.isInDragMode = false;
      },
      onDragOver(event: DragEvent) {},
      onDragDrop(event: DragEvent) {
        this.isInDragMode = false;
        if (event?.dataTransfer?.items) {
          // Use DataTransferItemList interface to access the file(s)
          for (let i = 0; i < event.dataTransfer.items.length; i += 1) {
            // If dropped items aren't files, reject them
            if (event.dataTransfer.items[i].kind === 'file') {
              const file = event.dataTransfer.items[i].getAsFile();
              if (file) {
                this.addMultipleFiles(file);
              }
            }
          }
        } else {
          // Use DataTransfer interface to access the file(s)
          const files = Array.from(event.dataTransfer?.files ?? [] as File[]);
          const { length } = files;
          for (let i = 0; i < length; i += 1) {
            files.forEach((file) => {
              this.addMultipleFiles(file);
            });
          }
        }
      },
      onDragEnd(event: DragEvent) {},
      onDragStart(event: DragEvent) {},
      calculateFileSize() {
      },
    },
  });
</script>
